
import { useState, useEffect } from 'react';
import ConsultationModal from './ConsultationModal';

const navLinks = [
  { label: 'Global Context', href: '#global-context' },
  { label: 'About', href: '#about' },
  { label: 'Activities', href: '#activities' },
  { label: 'Technologies', href: '#technologies' },
  { label: 'Production', href: '#production' },
  { label: 'Projects', href: '#projects' },
  { label: 'Contacts', href: '#contacts' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);

      const sections = navLinks.map((l) => l.href.replace('#', ''));
      let current = '';
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120) {
            current = id;
          }
        }
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileMenuOpen
            ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_0_0_rgba(0,102,255,0.08)]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 flex items-center justify-between h-[60px] lg:h-[72px]">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setMobileMenuOpen(false);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center gap-3 cursor-pointer flex-shrink-0"
          >
            <img
              src="https://static.readdy.ai/image/9df50203cd6382ba2542be027cac1a2d/7df65e5eb8b09727b0d588a7632889a2.png"
              alt="A&amp;R Consulting Logo"
              className="h-8 lg:h-9 w-auto"
            />
          </a>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = activeSection === link.href.replace('#', '');
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleClick(e, link.href)}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-[1px] transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    isActive
                      ? 'bg-[#0A1628] text-white'
                      : scrolled
                      ? 'text-[#0A1628]/60 hover:text-[#0A1628] hover:bg-[#F7FAFF]'
                      : 'text-[#0A1628]/50 hover:text-[#0A1628] hover:bg-[#0A1628]/5'
                  }`}
                >
                  {link.label}
                </a>
              );
            })}
          </div>

          {/* Mobile Right Side */}
          <div className="flex lg:hidden items-center gap-3">
            {/* Burger Button */}
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${
                mobileMenuOpen
                  ? 'bg-[#0A1628] text-white'
                  : scrolled
                  ? 'bg-[#F7FAFF] text-[#0A1628]'
                  : 'bg-[#0A1628]/10 text-[#0A1628]'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        ></div>

        {/* Drawer */}
        <div
          className={`absolute top-0 right-0 h-full w-[280px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Drawer Header */}
          <div className="flex items-center justify-between px-6 h-[60px] border-b border-gray-100">
            <span className="text-[11px] font-bold uppercase tracking-[2px] text-[#0A1628]/40">Menu</span>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg text-gray-600"></i>
            </button>
          </div>

          {/* Nav Links */}
          <div className="flex-1 overflow-y-auto py-4 px-4">
            {navLinks.map((link) => {
              const isActive = activeSection === link.href.replace('#', '');
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleClick(e, link.href)}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl mb-1 text-sm font-bold uppercase tracking-[1px] transition-all duration-200 cursor-pointer ${
                    isActive
                      ? 'bg-[#0A1628] text-white'
                      : 'text-[#0A1628]/70 hover:text-[#0A1628] hover:bg-[#F7FAFF]'
                  }`}
                >
                  {isActive && (
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0066FF] flex-shrink-0"></div>
                  )}
                  {link.label}
                </a>
              );
            })}
          </div>

          {/* Drawer Footer CTA */}
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                setIsModalOpen(true);
              }}
              className="w-full flex items-center justify-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white px-5 py-3.5 rounded-xl text-[11px] font-bold uppercase tracking-[1px] transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Request Consultation
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </button>
          </div>
        </div>
      </div>

      <ConsultationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
